import { useEffect, type RefObject } from 'react'
import {
  useMotionValue,
  useSpring,
  type MotionValue,
} from 'framer-motion'

interface Options {
  /** Pull radius in px. Cursor outside this distance from the element produces no movement. Default 80. */
  radius?: number
  /** Maximum displacement in px when the cursor is at the element centre. Default 12. */
  strength?: number
}

interface MagneticResult {
  x: MotionValue<number>
  y: MotionValue<number>
}

/**
 * Magnetic-button hook.
 *
 * Subscribes to window pointermove and translates the bound element toward
 * the cursor when the cursor is within `radius` px of the element bounds.
 * Movement is capped at `strength` and spring-damped so the element settles
 * cleanly after the cursor leaves.
 *
 * Disabled on coarse-pointer devices and when the user prefers reduced motion.
 */
export function useMagnetic<T extends HTMLElement>(
  ref: RefObject<T | null>,
  { radius = 80, strength = 12 }: Options = {},
): MagneticResult {
  const x = useMotionValue(0)
  const y = useMotionValue(0)
  const sx = useSpring(x, { stiffness: 300, damping: 25, mass: 0.5 })
  const sy = useSpring(y, { stiffness: 300, damping: 25, mass: 0.5 })

  useEffect(() => {
    const mq = typeof window.matchMedia === 'function' ? window.matchMedia.bind(window) : null
    // Coarse pointer (touch) — no hover, no magnetic.
    if (mq && mq('(hover: none)').matches) return
    // Reduced motion — disable.
    if (mq && mq('(prefers-reduced-motion: reduce)').matches) return

    const el = ref.current
    if (!el) return

    const handle = (e: PointerEvent) => {
      const rect = el.getBoundingClientRect()
      const cx = rect.left + rect.width / 2
      const cy = rect.top + rect.height / 2
      const dx = e.clientX - cx
      const dy = e.clientY - cy
      const dist = Math.hypot(dx, dy)
      // "Within radius of bounds" — measured from the closer edge for tall buttons.
      const halfW = rect.width / 2
      const halfH = rect.height / 2
      const edgeDist = Math.max(0, Math.hypot(Math.max(0, Math.abs(dx) - halfW), Math.max(0, Math.abs(dy) - halfH)))
      if (edgeDist > radius) {
        x.set(0)
        y.set(0)
        return
      }
      // Linear falloff from element centre, capped at strength px.
      const max = Math.max(halfW, halfH) + radius
      const factor = Math.min(1, dist / max)
      x.set((dx / max) * strength * (1 - factor * 0.4))
      y.set((dy / max) * strength * (1 - factor * 0.4))
    }

    const handleLeave = () => {
      x.set(0)
      y.set(0)
    }

    window.addEventListener('pointermove', handle, { passive: true })
    window.addEventListener('pointerleave', handleLeave, { passive: true })
    return () => {
      window.removeEventListener('pointermove', handle)
      window.removeEventListener('pointerleave', handleLeave)
    }
  }, [ref, radius, strength, x, y])

  return { x: sx, y: sy }
}
